/**
 * THE FIELD MAP'S REAL BASEMAP — self-hosted raster tiles, v23-A.
 *
 * ===========================================================================
 * SAME-ORIGIN TILES, OR NOTHING — the whole privacy argument, in one line
 * ===========================================================================
 * The tile URL is `/tiles/{z}/{x}/{y}.png` — SAME ORIGIN, no scheme, no host —
 * unless `VITE_TILE_URL` overrides it to another host GLOBAL ONE CONTROLS. The
 * browser therefore asks OUR server for tiles and our server serves them from
 * disk; the staff coordinates the viewport encodes never reach a third party.
 * `lint-tile-sources.mjs` fails the build if any known tile CDN or absolute
 * `{z}/{x}/{y}` template appears in the source — this component is written to
 * pass that gate, and it is the reason the default is a relative path.
 *
 * OSM ATTRIBUTION is a licence requirement for serving OSM-derived tiles and
 * is rendered by Leaflet's attribution control (text + the copyright link — a
 * link, not a tile source, and the lint knows the difference).
 *
 * ===========================================================================
 * THIS IS THE BASEMAP ONLY — every field-map rule still lives upstream
 * ===========================================================================
 * The PAYLOAD this draws is the same rule-14-masked, country-scoped,
 * purge-reachable `FieldMapPerson[]` the SVG map draws (the service drops
 * anyone the caller may not see raw, and a tracking-off country returns
 * nobody). This file only changes what the markers sit ON; it reads no new
 * data and relaxes no rule. Freshness is still the central claim — every
 * marker carries its age, computed against the payload's `server_time`.
 *
 * FALLBACK: `FieldMapPage` probes tile availability and mounts the v22 SVG map
 * instead when tiles are not being served (fresh install, tiles not generated
 * yet, or `VITE_TILE_URL` empty). Both paths are proven.
 */

import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { avatarHueIndex } from '../../components/ui/avatar-hue';
import type { FieldMapPerson } from './api';
import { ageSentence, freshnessOf } from './field-map-model';

/**
 * The tile template. Relative by default — the lint's whole point — and only
 * ever overridden to a host Global One runs. `import.meta.env` is inlined at
 * build time, so a deploy sets its own domain without touching source.
 */
export const TILE_URL_TEMPLATE: string =
  (import.meta.env.VITE_TILE_URL as string | undefined) ?? '/tiles/{z}/{x}/{y}.png';

/** The avatar system's eight hues, resolved to concrete rgb for the SVG pins. */
const HUE_VARS = [
  '--crf-avatar-1-bg',
  '--crf-avatar-2-bg',
  '--crf-avatar-3-bg',
  '--crf-avatar-4-bg',
  '--crf-avatar-5-bg',
  '--crf-avatar-6-bg',
  '--crf-avatar-7-bg',
  '--crf-avatar-8-bg',
] as const;

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/**
 * A Leaflet `divIcon` per person — the same identity→hue, initials, live/stale
 * treatment and ON-PIN AGE the SVG map draws, so a person reads identically on
 * either basemap. Live wears a pulsing ring; stale dresses hollow.
 */
function pinFor(person: FieldMapPerson, serverTime: string): L.DivIcon {
  const fresh = freshnessOf(person.live, person.captured_at, serverTime);
  const hueVar = HUE_VARS[avatarHueIndex(person.user_id)] ?? HUE_VARS[0];
  const fill = fresh === 'stale' ? 'var(--crf-bg-surface)' : `var(${hueVar})`;
  const ink = fresh === 'stale' ? 'var(--crf-text-secondary)' : 'var(--crf-brand-ink)';
  const ageText = fresh === 'live' ? 'live' : ageSentence(person.captured_at, serverTime);
  const ring =
    fresh === 'live'
      ? '<span class="crf-pin-ring" aria-hidden="true"></span>'
      : '';
  return L.divIcon({
    className: 'crf-field-pin',
    html:
      `<span class="crf-pin-body" style="background:${fill};color:${ink};` +
      `border-color:${fresh === 'stale' ? 'var(--crf-text-secondary)' : 'var(--crf-brand-ink)'}">` +
      `${initials(person.name)}</span>${ring}` +
      `<span class="crf-pin-age" style="color:${fresh === 'live' ? 'var(--crf-status-success-text)' : 'var(--crf-text-secondary)'}">${ageText}</span>`,
    iconSize: [34, 34],
    iconAnchor: [17, 17],
  });
}

export function FieldTileMap({
  people,
  serverTime,
  countryCode,
  onSelect,
}: {
  people: FieldMapPerson[];
  serverTime: string;
  countryCode: string;
  onSelect: (userId: string) => void;
}): React.JSX.Element {
  const holder = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const layerRef = useRef<L.LayerGroup | null>(null);

  // Mount the Leaflet map ONCE — tiles and controls do not depend on the data.
  useEffect(() => {
    if (holder.current === null || mapRef.current !== null) return;
    const map = L.map(holder.current, {
      attributionControl: true,
      zoomControl: true,
    });
    L.tileLayer(TILE_URL_TEMPLATE, {
      maxZoom: 18,
      // The licence requirement. Text + copyright link — Leaflet renders it in
      // the corner; the lint permits the copyright link (not a tile source).
      attribution:
        '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a> contributors',
    }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      layerRef.current = null;
    };
  }, []);

  // Redraw markers when the data changes, and fit the view to the people.
  useEffect(() => {
    const map = mapRef.current;
    const layer = layerRef.current;
    if (map === null || layer === null) return;
    layer.clearLayers();
    const points: L.LatLngExpression[] = [];
    for (const person of people) {
      const lat = Number(person.lat);
      const lng = Number(person.lng);
      points.push([lat, lng]);
      const marker = L.marker([lat, lng], {
        icon: pinFor(person, serverTime),
        title: person.name,
        alt: `${person.name} — ${
          person.live ? 'live' : ageSentence(person.captured_at, serverTime)
        }`,
      });
      marker.on('click', () => {
        onSelect(person.user_id);
      });
      marker.addTo(layer);
    }
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points).pad(0.35), { maxZoom: 15 });
    }
  }, [people, serverTime, onSelect]);

  return (
    <div
      ref={holder}
      role="img"
      aria-label={`Field map of ${countryCode}, ${String(people.length)} people plotted on self-hosted tiles`}
      className="h-[440px] w-full overflow-hidden rounded-sm"
      data-testid="crf-field-tilemap"
    />
  );
}
